import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TalkDebate, TalkContribution, TalkCategory, useTalkStore } from "@/data/talkStore";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { talkLocales, TalkLocales } from "@/i18n/talkLocales";
import { useToast } from "@/hooks/use-toast";
import AuthModal from "@/components/AuthModal";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, Presentation, Shield, Building2, ArrowLeft, ArrowRight, MessageCircle, Mic, ThumbsUp, ThumbsDown, History, X, CheckCircle2, AlertCircle } from "lucide-react";
import { useSettingsStore } from "@/data/settingsStore";

const CatIcon = {
  CITOYEN: Users,
  ONG: Presentation,
  PARTI: Shield,
  INSTITUTION: Building2
};

export default function TalkPage() {
  const { lang, isRtl } = useI18n();
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  
  const t = talkLocales[lang as TalkLocales] || talkLocales.fr;

  const { 
    debates: allDebates, 
    contributions: allContributions, 
    likesEnabled,
    interactions,
    fetchDebates,
    fetchContributions,
    submitContribution,
    toggleTalkContributionInteraction
  } = useTalkStore();

  const settingsObj = useSettingsStore(state => state.settings);
  const globalFormat = settingsObj['kaynin_talk_global_format'] || 'font-normal text-foreground';

  const [activeDebate, setActiveDebate] = useState<TalkDebate | null>(null);
  const [contributions, setContributions] = useState<TalkContribution[]>([]);
  const [previousDebates, setPreviousDebates] = useState<TalkDebate[]>([]);
  
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pendingOpenForm, setPendingOpenForm] = useState(false);

  // Form state
  const [text, setText] = useState("");
  const [proposedQuestion, setProposedQuestion] = useState("");
  const [proposedDomain, setProposedDomain] = useState("");
  const [proposedDomainOther, setProposedDomainOther] = useState("");
  const [prefLang, setPrefLang] = useState(lang || "fr");
  const [ethicsChecked, setEthicsChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const MAX_CHARS = 350;

  useEffect(() => {
    fetchDebates();
    fetchContributions();
  }, [fetchDebates, fetchContributions]);

  useEffect(() => {
     if (allDebates.length > 0) {
       const sortedDebates = [...allDebates].sort((a,b) => {
         const dateA = new Date(a.createdAt.replace(' ', 'T')).getTime();
         const dateB = new Date(b.createdAt.replace(' ', 'T')).getTime();
         return dateB - dateA;
       });
       const active = sortedDebates.find(d => d.status === "publie") || null;
       
       setActiveDebate(active);
       
       if (active) {
         setContributions(allContributions.filter(c => c.debateId === active.id && c.status === "valide"));
       }
       setPreviousDebates(sortedDebates.filter(d => d.status === "cloture").slice(0, 5));
     }
  }, [allDebates, allContributions]);

  useEffect(() => {
    if (isAuthenticated && pendingOpenForm) {
      setPendingOpenForm(false);
      setIsFormOpen(true);
    }
  }, [isAuthenticated, pendingOpenForm]);
  
  // Mettre à jour l'alignement par défaut quand la langue de navigation change
  useEffect(() => {
    setPrefLang(lang);
  }, [lang]);

  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});

  const toggleExpand = (id: string) => {
    setExpandedComments(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getFormattedName = (name: string, category: string) => {
    if (!name || category !== 'CITOYEN') return name;
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
      return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}.`;
    }
    return name;
  };

  const getFormattedDesc = (desc: any) => {
    if (typeof desc !== 'string') return desc;
    if (desc.includes("Débat simulé pour la fin Mars.")) return "Débat simulé pour la fin Mars.";
    if (desc.includes("نقاش محاكى لنهاية شهر مارس.")) return "نقاش محاكى لنهاية شهر مارس.";
    if (desc.includes("ⴰⵎⵙⵍⴰⵢ ⵉ ⵜⴳⴳⴰⵔⴰ ⵏ ⵎⴰⵕⵚ.")) return "ⴰⵎⵙⵍⴰⵢ ⵉ ⵜⴳⴳⴰⵔⴰ ⵏ ⵎⴰⵕⵚ.";
    return desc;
  };

  const handlePrendreLaParole = () => {
    if (isAuthenticated) {
      const userHasContributed = allContributions.some(c => c.debateId === activeDebate!.id && c.authorId === user!.id);
      
      if (userHasContributed) {
        toast({
          title: t.alreadyParticipated,
          description: t.alreadyParticipatedDesc
        });
        return;
      }

      setSubmitted(false);
      setText("");
      setProposedQuestion("");
      setProposedDomain("");
      setProposedDomainOther("");
      setEthicsChecked(false);
      setError("");
      setIsFormOpen(true);
    } else {
      setPendingOpenForm(true);
      setIsAuthModalOpen(true);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_CHARS) {
      setText(e.target.value);
    }
  };

  const handleToggleLike = (id: string) => {
    const current = interactions[id];
    const newType = current === 'like' ? 'none' : 'like';
    toggleTalkContributionInteraction(id, newType);
  };

  const handleToggleDislike = (id: string) => {
    const current = interactions[id];
    const newType = current === 'dislike' ? 'none' : 'dislike';
    toggleTalkContributionInteraction(id, newType);
  };

  const handleSubmit = async () => {
    if (!text.trim()) { setError(t.errEmpty || "Le message ne peut pas être vide."); return; }
    if (!ethicsChecked) { setError(t.errEthics || "Veuillez accepter la charte éthique."); return; }

    const finalDomain = proposedDomain === "Autre" ? proposedDomainOther.trim() : proposedDomain;
    if (finalDomain && finalDomain.split(/\s+/).filter(Boolean).length > 2) {
       setError(t.errDomain || "Le domaine libre ne doit pas dépasser 2 mots.");
       return;
    }

    try {
      await submitContribution({
        debateId: activeDebate!.id,
        authorId: user!.id,
        authorName: user!.name,
        authorCategory: (user!.role === "CITIZEN" ? "CITOYEN" : user!.role) as TalkCategory,
        authorColor: user!.role === "ONG" ? "#10b981" : "#6366f1",
        authorShade: "clair",
        genre: user?.genre as "femme" | "homme" | "non_precise" | undefined,
        authorActivity: user?.profession || user?.categorieSociopro,
        text: text.trim(),
        preferredLanguage: prefLang,
        proposedQuestion: proposedQuestion.trim() || undefined,
        proposedDomain: finalDomain || undefined
      });
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message || "Une erreur est survenue lors de l'envoi.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={isRtl ? 'rtl' : 'ltr'}>
      <Navbar />

      <main className="flex-1 pt-24 pb-16 relative">
         <div className="absolute top-0 inset-x-0 h-64 bg-gradient-to-b from-indigo-50 to-background dark:from-indigo-950/20 -z-10"></div>
         
         <div className="container px-4 md:px-6">
            <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors" asChild>
               <Link to="/accueil-talk">
                 <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />
                 {lang === 'ar' ? "العودة للرئيسية" : "Retour à l'accueil"}
               </Link>
            </Button>

            {!activeDebate ? (
               <div className="text-center py-20 text-muted-foreground">{t.closedDesc}</div>
            ) : (
               <>
                  <div className="bg-card border border-border shadow-sm rounded-3xl p-6 md:p-10 mb-10 text-center relative overflow-hidden">
                     <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-body text-sm font-extrabold mb-1.5 border border-indigo-200 dark:border-indigo-800">
                        <MessageCircle className="w-4 h-4" /> 
                        {t.talkTitle} : {lang === 'ar' ? "نقاشات افتراضية" : "Débats virtuels"}
                     </span>
                     <h1 className="text-2xl md:text-4xl lg:text-5xl font-display font-extrabold text-foreground mb-4">
                        {t.qWeek} <span className="text-indigo-600 dark:text-indigo-400">{lang === 'ar' ? (activeDebate.titleAr || `[ترجمة آلية] ${activeDebate.title}`) : lang === 'zgh' ? (activeDebate.titleZgh || `[ⴰⵙⵓⵖⵍ ⴰⵡⵜⵓⵎⴰⵜⴰⵏ] ${activeDebate.title}`) : activeDebate.title}</span>
                     </h1>
                     <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto mb-8 whitespace-pre-wrap">
                        {getFormattedDesc(lang === 'ar' ? (activeDebate.descriptionAr || `[ترجمة آلية] ${activeDebate.description}`) : lang === 'zgh' ? (activeDebate.descriptionZgh || `[ⴰⵙⵓⵖⵍ ⴰⵡⵜⵓⵎⴰⵜⴰⵏ] ${activeDebate.description}`) : activeDebate.description)}
                     </p>

                     <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handlePrendreLaParole}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-indigo-500/30 flex items-center justify-center mx-auto gap-2"
                     >
                        <Mic className="w-5 h-5" />
                        {t.takeFloor}
                     </motion.button>
                  </div>

                  <div className="mb-16">
                     <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl md:text-2xl font-display font-bold">
                           {t.allComments} ({contributions.length})
                        </h2>
                     </div>

                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                        {contributions.map((cont, index) => {
                           const Icon = CatIcon[cont.authorCategory] || Users;
                           return (
                              <motion.div 
                                 key={cont.id}
                                 initial={{ opacity: 0 }}
                                 whileInView={{ opacity: 1 }}
                                 viewport={{ once: true, margin: "50px" }}
                                 transition={{ duration: 0.3 }}
                                 className="bg-card rounded-2xl p-5 border shadow-sm relative overflow-hidden flex flex-col hover:shadow-md transition-all group"
                                 style={{ borderColor: `${cont.authorColor}40` }}
                              >
                                 <div className="absolute top-0 right-0 w-24 h-24 opacity-[0.03] group-hover:opacity-10 transition-opacity" style={{ backgroundColor: cont.authorColor, borderRadius: '0 0 0 100%' }}></div>
                                 <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${cont.authorColor}15`, color: cont.authorColor }}>
                                       <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                       <p className="font-bold text-sm text-foreground truncate"><span dir="ltr">{getFormattedName(cont.authorName, cont.authorCategory)}</span></p>
                                       <p className="text-xs text-muted-foreground capitalize truncate">
                                          {cont.authorCategory === 'CITOYEN' && cont.authorActivity ? cont.authorActivity : (cont.authorCategory === 'CITOYEN' ? (lang === 'ar' ? 'مواطن(ة)' : 'Citoyen') : cont.authorCategory.toLowerCase())}
                                          {cont.genre && (
                                             <>
                                               <span className="mx-1">•</span>
                                               <span className={cont.genre === 'homme' ? 'text-blue-500 font-bold text-sm' : cont.genre === 'femme' ? 'text-pink-500 font-bold text-sm' : ''}>
                                                 {cont.genre === 'homme' ? '♂️' : cont.genre === 'femme' ? '♀️' : ''}
                                               </span>
                                             </>
                                          )}
                                       </p>
                                       {false && (
                                          <p className="text-[10px] text-muted-foreground/80 italic truncate">{cont.authorActivity}</p>
                                       )}
                                    </div>
                                 </div>
                                 <div className="mb-6 mt-auto">
                                   <p dir={cont.preferredLanguage === 'ar' ? 'rtl' : 'ltr'} className={`text-sm font-body leading-relaxed ${globalFormat} ${expandedComments[cont.id] ? '' : 'line-clamp-4'}`}>
                                      "{cont.text}"
                                   </p>
                                   {cont.text && cont.text.length > 120 && (
                                     <button onClick={() => toggleExpand(cont.id)} className={`text-xs text-indigo-600 dark:text-indigo-400 font-semibold mt-1 hover:underline ${cont.preferredLanguage === 'ar' ? 'float-left' : 'float-right'}`}>
                                       {expandedComments[cont.id] ? (lang === 'ar' ? 'عرض أقل' : 'Voir moins') : (lang === 'ar' ? 'عرض المزيد' : 'Voir plus')}
                                     </button>
                                   )}
                                 </div>
                                 <div className="flex items-center justify-between border-t border-border/50 pt-3 mt-auto">
                                    <p className="text-[11px] text-muted-foreground">{new Date(cont.createdAt).toLocaleDateString()}</p>
                                    <div className="flex items-center gap-3">
                                       <button 
                                          onClick={() => handleToggleLike(cont.id)}
                                          className={`flex items-center gap-1.5 text-[11px] font-semibold p-1 rounded-md transition-all ${interactions[cont.id] === 'like' ? 'scale-110' : 'hover:scale-110 hover:bg-muted'}`} 
                                          style={{ color: interactions[cont.id] === 'like' ? cont.authorColor : 'currentColor' }}
                                       >
                                          <ThumbsUp className={`w-3.5 h-3.5 ${interactions[cont.id] === 'like' ? 'fill-current' : ''}`} />
                                          <span>{cont.likes}</span>
                                       </button>
                                       <button 
                                          onClick={() => handleToggleDislike(cont.id)}
                                          className={`flex items-center gap-1.5 text-[11px] font-semibold p-1 rounded-md transition-all ${interactions[cont.id] === 'dislike' ? 'scale-110 text-destructive' : 'hover:scale-110 hover:bg-muted text-muted-foreground'}`}
                                       >
                                          <ThumbsDown className={`w-3.5 h-3.5 ${interactions[cont.id] === 'dislike' ? 'fill-current' : ''}`} />
                                          <span>{cont.dislikes || 0}</span>
                                       </button>
                                    </div>
                                 </div>
                              </motion.div>
                           )
                        })}
                        {contributions.length === 0 && (
                           <div className="col-span-full py-12 text-center border border-dashed rounded-3xl text-muted-foreground">
                              {lang === 'ar' ? "لا توجد تعليقات بعد. كن أول من يأخذ الكلمة!" : "Aucun commentaire pour l'instant. Soyez le premier à prendre la parole !"}
                           </div>
                        )}
                     </div>
                  </div>

                </>
             )}

             <div className="pt-10 border-t border-border mt-10">
                {previousDebates.length > 0 && (
                   <div className="mb-8">
                      <h2 className="text-xl md:text-2xl font-display font-bold flex items-center gap-2 mb-6">
                         <History className="w-5 h-5 text-indigo-500" />
                         {lang === 'ar' ? "الأسئلة السابقة (هذا الشهر)" : "Questions précédentes (ce mois-ci)"}
                      </h2>
                      <div className="flex flex-col gap-3">
                         {previousDebates.map((pd, idx) => (
                            <motion.div 
                               initial={{ opacity: 0 }}
                               whileInView={{ opacity: 1 }}
                               viewport={{ once: true, margin: "50px" }}
                               transition={{ duration: 0.3 }}
                               key={pd.id} 
                               className="p-4 bg-muted/40 hover:bg-muted border border-border rounded-xl flex flex-col md:flex-row justify-between md:items-center gap-4 transition-colors group"
                            >
                               <div className="flex-1">
                                  <h4 className="font-bold text-foreground mb-1 group-hover:text-indigo-600 transition-colors">{lang === 'ar' ? (pd.titleAr || pd.title) : lang === 'zgh' ? (pd.titleZgh || pd.title) : pd.title}</h4>
                                  <p className="text-xs text-muted-foreground mb-3 whitespace-pre-wrap">{getFormattedDesc(lang === 'ar' ? (pd.descriptionAr || pd.description) : lang === 'zgh' ? (pd.descriptionZgh || pd.description) : pd.description)}</p>
                                  
                                  {/* Section admin conclusion */}
                                  {pd.adminConclusion && (
                                     <div className="bg-background border border-indigo-100 dark:border-indigo-900/50 rounded-lg p-3 text-sm flex gap-2 mb-3">
                                        <Shield className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                                        <p className="text-foreground/90 italic leading-relaxed">
                                           <span className="font-semibold text-indigo-600 dark:text-indigo-400 mr-2">{t.adminConc}</span>
                                           {pd.adminConclusion}
                                        </p>
                                     </div>
                                  )}
                                  
                                  {/* Section statistiques publiques */}
                                  {(pd.showStats_participants !== false || pd.showStats_gender !== false || pd.showStats_likes !== false) && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {(() => {
                                        const dConts = allContributions.filter(c => c.debateId === pd.id && c.status === "valide");
                                        const h = dConts.filter(c => c.genre === "homme").length;
                                        const f = dConts.filter(c => c.genre === "femme").length;
                                        const l = dConts.reduce((a, b) => a + b.likes, 0);

                                        return (
                                          <>
                                            {pd.showStats_participants !== false && (
                                              <span className="text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-0.5 rounded-full font-semibold">
                                                {lang === 'ar' ? `المشاركون: ${dConts.length}` : `${dConts.length} Participants`}
                                              </span>
                                            )}
                                            {pd.showStats_gender !== false && (
                                              <span className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-0.5 rounded-full font-semibold">
                                                {h} H / {f} F
                                              </span>
                                            )}
                                            {pd.showStats_likes !== false && (
                                              <>
                                                <span className="text-[10px] bg-pink-50 text-pink-600 border border-pink-100 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                                                  <ThumbsUp className="w-3 h-3" /> {l} Likes
                                                </span>
                                                <span className="text-[10px] bg-rose-50 text-rose-600 border border-rose-100 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                                                  <ThumbsDown className="w-3 h-3" /> {dConts.reduce((a, b) => a + (b.dislikes || 0), 0)} Dislikes
                                                </span>
                                              </>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                               </div>
                               <div className="flex items-center gap-3 shrink-0 text-xs font-semibold text-indigo-600 p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
                                  {t.debateClosed}
                               </div>
                            </motion.div>
                         ))}
                      </div>
                   </div>
                )}
                
                <div className="flex justify-center">
                   <Link to="/talk/archives" className="inline-flex items-center gap-2 px-8 py-3.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 font-bold rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors border border-indigo-200 dark:border-indigo-800 shadow-sm hover:shadow-md">
                      <History className="w-5 h-5" />
                      {lang === 'ar' ? "الاطلاع على أرشيف الأشهر السابقة" : "Consulter l'archive des mois passés"}
                   </Link>
                </div>
             </div>
         </div>
      </main>

      <Footer />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => { setIsAuthModalOpen(false); setPendingOpenForm(false); }} />

      <AnimatePresence>
        {isFormOpen && activeDebate && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(evt) => { if (evt.target === evt.currentTarget) setIsFormOpen(false); }}
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-card border border-border rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 shrink-0">
                <button onClick={() => setIsFormOpen(false)} className={`absolute top-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors ${isRtl ? 'left-4' : 'right-4'}`}>
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-white font-display font-bold text-lg">{t.formTitle}</h3>
                <p className="text-white/80 text-xs font-body mt-1">
                   {t.formDesc}
                </p>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar">
                {!submitted ? (
                  <div className="space-y-5">
                    <div className="space-y-2">
                       <label className="text-sm font-semibold">{t.prefLangLabel}</label>
                       <select value={prefLang} onChange={e => setPrefLang(e.target.value as "fr" | "ar" | "zgh")} className="w-full h-10 rounded-xl border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none">
                          <option value="fr">Français</option>
                          <option value="ar">العربية</option>
                          <option value="zgh">Tamazight</option>
                       </select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-end">
                         <label className="text-sm font-semibold">{t.commentLabel}</label>
                         <span className={`text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full ${text.length >= MAX_CHARS ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                           {text.length} / {MAX_CHARS}
                         </span>
                      </div>
                      <textarea
                        value={text}
                        onChange={handleTextChange}
                        dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                        placeholder={t.commentPlaceholder}
                        className={`w-full h-36 resize-none rounded-xl border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 custom-scrollbar leading-relaxed ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       <div className="space-y-2">
                          <label className="text-sm font-semibold">{t.domainLabel}</label>
                          <select 
                             value={proposedDomain} 
                             onChange={e => setProposedDomain(e.target.value)} 
                             dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                             className={`w-full h-10 rounded-xl border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none custom-scrollbar ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                          >
                             <option value="">{t.domainSelect}</option>
                             <option value="Éducation">{t.domEducation}</option>
                             <option value="Santé">{t.domSante}</option>
                             <option value="Économie / Emploi">{t.domEco}</option>
                             <option value="Infrastructure">{t.domInfra}</option>
                             <option value="Transport">{t.domTransport}</option>
                             <option value="Environnement">{t.domEnv}</option>
                             <option value="Justice">{t.domJustice}</option>
                             <option value="Culture">{t.domCulture}</option>
                             <option value="Digitale">{t.domDigitale}</option>
                             <option value="Autre">{t.domainOther}</option>
                          </select>
                          {proposedDomain === "Autre" && (
                            <input 
                              type="text" 
                              value={proposedDomainOther}
                              onChange={e => setProposedDomainOther(e.target.value)}
                              dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                              placeholder={t.domainOtherPlaceholder}
                              className={`w-full h-10 mt-2 rounded-xl border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                            />
                          )}
                       </div>
                       <div className="space-y-2">
                          <div className="flex justify-between items-end">
                             <label className="text-sm font-semibold">{t.questionLabel}</label>
                             <span className={`text-[10px] font-bold ${proposedQuestion.length >= 150 ? 'text-destructive' : 'text-muted-foreground'}`}>{proposedQuestion.length}/150</span>
                          </div>
                          <textarea 
                             maxLength={150}
                             value={proposedQuestion}
                             onChange={e => setProposedQuestion(e.target.value)}
                             dir={prefLang === 'ar' ? 'rtl' : 'ltr'}
                             placeholder={t.questionPlaceholder}
                             className={`w-full h-20 resize-none rounded-xl border border-input bg-background p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none custom-scrollbar leading-relaxed ${prefLang === 'ar' ? 'text-right' : 'text-left'}`}
                          />
                       </div>
                    </div>

                    <div className={`flex items-start gap-3 p-3 rounded-xl border ${error && !ethicsChecked ? "border-destructive bg-destructive/5" : "border-border bg-muted/30"}`}>
                      <input type="checkbox" id="ethics-talk" checked={ethicsChecked} onChange={e => setEthicsChecked(e.target.checked)} className="mt-0.5 accent-indigo-600 w-4 h-4 shrink-0 cursor-pointer" />
                      <label htmlFor="ethics-talk" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
                        {t.ethics}
                      </label>
                    </div>

                    {error && <p className="text-xs text-destructive flex items-start gap-1 p-2 bg-destructive/10 rounded-lg"><AlertCircle className="w-4 h-4 shrink-0"/><span>{error}</span></p>}

                    <button onClick={handleSubmit} className="w-full py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2">
                      <Mic className="w-4 h-4" /> {t.publish}
                    </button>
                    <p className="text-[10px] text-center text-muted-foreground italic px-4">
                       {t.moderation}
                    </p>
                  </div>
                ) : (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5 shadow-inner">
                      <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                    </div>
                    <h4 className="font-display font-bold text-xl mb-3">{t.successTitle}</h4>
                    <p className="text-sm text-muted-foreground mb-8 max-w-[250px] mx-auto leading-relaxed">
                      {t.successDesc}
                    </p>
                    <button onClick={() => setIsFormOpen(false)} className="px-8 py-3 rounded-full bg-slate-900 hover:bg-slate-800 transition-colors border text-white font-bold text-sm shadow-md">
                      {t.close}
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
